'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import type { BoardPointer } from '@puzzlio/core';

interface GestureSurfaceProps {
  rows: number;
  cols: number;
  /** Every phase of a gesture arrives here, already resolved to a cell. */
  onPointer: (pointer: BoardPointer) => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  /**
   * Whether the board takes input at all. A game that stops accepting moves once the puzzle is
   * finished passes false. On native it
   * matters for a second reason: a disabled gesture lets a scrolling page have the drag instead.
   */
  enabled?: boolean;
  /**
   * Whether a right-click on the board is kept from opening the browser's menu. Defaults to `enabled`,
   * but a game still in progress passes true while it has the board locked too — a tutorial step
   * that is only talking, say — since that board is still something the player plays on.
   */
  blockContextMenu?: boolean;
}

/**
 * One pointer-handling element covering the whole board, replacing per-cell handlers.
 *
 * Which cell is under the pointer comes from the surface's own bounds and the pointer's client
 * coordinates, never from the event target. That matters on touch, where the browser silently
 * captures the gesture to the element the finger first landed on: subsequent moves keep firing
 * against that element no matter where the finger travels, so target-based hit testing reports the
 * start cell forever. Measuring against the surface reports the cell actually under the finger.
 *
 * The DOM implementation. A native GestureSurface wraps a pan gesture whose coordinates are already
 * surface-local, and feeds the same BoardPointers to the same reducers.
 */
export const GestureSurface: React.FC<GestureSurfaceProps> = ({
  rows,
  cols,
  onPointer,
  className,
  style,
  children,
  enabled = true,
  blockContextMenu = enabled,
}) => {
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  /** Measured once per gesture: a board can't resize mid-drag, and this keeps moves off the layout path. */
  const boundsRef = useRef<DOMRect | null>(null);
  const activeRef = useRef(false);
  /** Set by a right-button press, and consumed by the menu that press opens. See the effect below. */
  const secondaryPressRef = useRef(false);
  const blockContextMenuRef = useRef(blockContextMenu);
  blockContextMenuRef.current = blockContextMenu;
  const onPointerRef = useRef(onPointer);
  onPointerRef.current = onPointer;

  /*
    The menu is refused from the window rather than from the surface. The press that finishes a
    tutorial step or the puzzle locks the board, and a locked board has pointer-events off, so the
    menu that press opens — after pointerdown on macOS, after pointerup on Windows — goes to whatever is
    underneath. The press itself is remembered instead, and its menu refused wherever it lands.

    Releases are heard on the window for the same reason: a pointerup over a locked board never
    reaches it, and a gesture that never ends leaves its cell pressed.
  */
  useEffect(() => {
    const onPointerDown = () => {
      // Capture phase on the window, so this runs before the surface records a new press.
      secondaryPressRef.current = false;
    };
    const onPointerEnd = () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      boundsRef.current = null;
      onPointerRef.current({ kind: 'end', row: 0, col: 0, fx: 0, fy: 0, secondary: false });
    };
    const onContextMenu = (e: MouseEvent) => {
      const bounds = surfaceRef.current?.getBoundingClientRect();
      const overBoard =
        !!bounds &&
        e.clientX >= bounds.left &&
        e.clientX < bounds.right &&
        e.clientY >= bounds.top &&
        e.clientY < bounds.bottom;
      if (secondaryPressRef.current || (blockContextMenuRef.current && overBoard)) e.preventDefault();
      secondaryPressRef.current = false;
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('pointerup', onPointerEnd, true);
    window.addEventListener('pointercancel', onPointerEnd, true);
    window.addEventListener('contextmenu', onContextMenu, true);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('pointerup', onPointerEnd, true);
      window.removeEventListener('pointercancel', onPointerEnd, true);
      window.removeEventListener('contextmenu', onContextMenu, true);
    };
  }, []);

  const cellAt = useCallback(
    (clientX: number, clientY: number): Omit<BoardPointer, 'kind' | 'secondary'> | null => {
      const bounds = boundsRef.current ?? surfaceRef.current?.getBoundingClientRect();
      if (!bounds || bounds.width === 0 || bounds.height === 0) return null;

      const colF = ((clientX - bounds.left) / bounds.width) * cols;
      const rowF = ((clientY - bounds.top) / bounds.height) * rows;
      const col = Math.max(0, Math.min(Math.floor(colF), cols - 1));
      const row = Math.max(0, Math.min(Math.floor(rowF), rows - 1));

      // Measured against the clamped cell, so a pointer dragged past the edge reports a position
      // inside the last cell rather than one beyond it.
      return { row, col, fx: Math.max(0, Math.min(colF - col, 1)), fy: Math.max(0, Math.min(rowF - row, 1)) };
    },
    [rows, cols]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.cancelable) e.preventDefault();
      boundsRef.current = surfaceRef.current?.getBoundingClientRect() ?? null;

      const cell = cellAt(e.clientX, e.clientY);
      if (!cell) return;

      activeRef.current = true;
      secondaryPressRef.current = e.button === 2;
      onPointer({ kind: 'start', ...cell, secondary: e.button === 2 });
    },
    [cellAt, onPointer]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!activeRef.current) return;
      if (e.cancelable) e.preventDefault();

      const cell = cellAt(e.clientX, e.clientY);
      if (!cell) return;

      onPointer({ kind: 'move', ...cell, secondary: e.button === 2 });
    },
    [cellAt, onPointer]
  );

  const handlePointerLeave = useCallback(
    (e: React.PointerEvent) => {
      if (!activeRef.current) return;
      if (e.cancelable) e.preventDefault();

      activeRef.current = false;
      boundsRef.current = null;
      onPointer({ kind: 'end', row: 0, col: 0, fx: 0, fy: 0, secondary: false });
    },
    [onPointer]
  );

  return (
    <div
      ref={surfaceRef}
      // PUZZLE_GRID_ATTR: what the burst measures and what tests address the board by.
      data-puzzle-grid=""
      className={className}
      /*
        touch-action is what keeps a drag across the board from scrolling the page instead of
        painting; preventDefault on a pointer event does not, since the browser has already
        committed the gesture to scrolling by the time those fire. A board that isn't taking input
        is just a picture, so it hands both touch and the drag back to the page.

        The surface is always its own compositing layer. A board centred on a fractional pixel is
        drawn on different device pixels in place than on a layer, and a transform anywhere inside
        (a pressed cell shrinking, an ✕ popping in) promotes the surface for as long as it runs. Left
        to that, the whole grid shifts back and forth under a frame that stays put.
      */
      style={
        enabled
          ? { touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', willChange: 'transform', ...style }
          : { willChange: 'transform', ...style, pointerEvents: 'none' }
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      // A gesture that leaves the board ends there, matching the old per-grid onPointerLeave.
      onPointerLeave={handlePointerLeave}
    >
      {children}
    </div>
  );
};
